import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import useAuth from "../useAuth";
import axios from "axios";
import { server } from "../server";
import { motion, AnimatePresence } from "framer-motion";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  PieChart,
  Pie,
} from "recharts";
import {
  FaUserGraduate,
  FaChalkboardTeacher,
  FaBuilding,
  FaBullhorn,
  FaCalendarAlt,
  FaMoneyBillWave,
  FaUsers,
  FaBookOpen,
  FaArrowRight,
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaExclamationTriangle,
  FaSyncAlt,
  FaMale,
  FaFemale,
  FaReceipt,
  FaImages,
  FaUserShield,
  FaClipboardList,
  FaChevronRight,
  FaArrowUp,
  FaArrowDown,
  FaGraduationCap,
  FaChartLine,
  FaWallet,
} from "react-icons/fa";

/* ═══════════════════════════════════════════════════════════ HELPERS */
const fmt = (n) => (n ?? 0).toLocaleString("en-IN");
const fmtC = (n) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(n ?? 0);
const fmtDate = (d) =>
  new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
const ago = (d) => {
  const m = Math.floor((Date.now() - new Date(d)) / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};
const greetMsg = () => {
  const h = new Date().getHours();
  if (h < 12) return "Good Morning";
  if (h < 17) return "Good Afternoon";
  return "Good Evening";
};

/* ═══════════════════════════════════════════════════════════ SKELETON */
const SK = ({ cls = "" }) => (
  <div
    className={`rounded-xl ${cls}`}
    style={{
      background: "linear-gradient(90deg,#f0f4f8 25%,#e8edf2 50%,#f0f4f8 75%)",
      backgroundSize: "200% 100%",
      animation: "shimmer 1.4s infinite",
    }}
  />
);

/* ═══════════════════════════════════════════════════════════ CARD */
const Card = ({ children, className = "", onClick, style = {} }) => (
  <motion.div
    onClick={onClick}
    whileHover={
      onClick ? { y: -2, boxShadow: "0 12px 32px -6px rgba(0,0,0,0.12)" } : {}
    }
    transition={{ duration: 0.18, ease: "easeOut" }}
    className={`bg-white dark:bg-slate-900 rounded-2xl border border-slate-200/80 dark:border-slate-800 overflow-hidden ${onClick ? "cursor-pointer" : ""} ${className}`}
    style={{ boxShadow: "0 1px 4px rgba(0,0,0,0.06)", ...style }}
  >
    {children}
  </motion.div>
);

/* ═══════════════════════════════════════════════════════════ CARD HEADER */
const CardHeader = ({ title, sub, action, actionLabel = "View All" }) => (
  <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-50">
    <div>
      <h3 className="text-[13px] font-bold text-slate-800 tracking-tight">
        {title}
      </h3>
      {sub && <p className="text-[11px] text-slate-400 mt-0.5">{sub}</p>}
    </div>
    {action && (
      <button
        onClick={action}
        className="flex items-center gap-1.5 text-[11px] font-semibold text-indigo-600
          hover:text-white hover:bg-indigo-600 bg-indigo-50 px-3 py-1.5 rounded-lg transition-all duration-150"
      >
        {actionLabel} <FaArrowRight className="text-[9px]" />
      </button>
    )}
  </div>
);

/* ═══════════════════════════════════════════════════════════ BADGE */
const Badge = ({ text, variant = "blue" }) => {
  const M = {
    blue: "bg-indigo-50 text-indigo-700",
    green: "bg-emerald-50 text-emerald-700",
    red: "bg-rose-50 text-rose-600",
    orange: "bg-amber-50 text-amber-700",
    gray: "bg-slate-100 text-slate-500",
  };
  return (
    <span
      className={`inline-block text-[10px] font-bold px-2 py-0.5 rounded-full ${M[variant] || M.gray}`}
    >
      {text}
    </span>
  );
};

/* ═══════════════════════════════════════════════════════════ AVATAR */
const Avatar = ({ name, gender }) => {
  const bg =
    gender === "Female"
      ? "linear-gradient(135deg,#f472b6,#e11d48)"
      : "linear-gradient(135deg,#818cf8,#4f46e5)";
  return (
    <div
      className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0 shadow-sm"
      style={{ background: bg }}
    >
      {name?.[0]?.toUpperCase() || "?"}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════ CIRCULAR PROGRESS */
const Ring = ({ value, max, color, label, size = 80, stroke = 8 }) => {
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const pct = max > 0 ? Math.min(value / max, 1) : 0;
  const offset = circ * (1 - pct);
  return (
    <div className="flex flex-col items-center gap-1.5">
      <div className="relative" style={{ width: size, height: size }}>
        <svg
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          style={{ transform: "rotate(-90deg)" }}
        >
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke="#F1F5F9"
            strokeWidth={stroke}
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke={color}
            strokeWidth={stroke}
            strokeDasharray={`${circ} ${circ}`}
            strokeDashoffset={offset}
            strokeLinecap="round"
            style={{
              transition: "stroke-dashoffset 1s cubic-bezier(.4,0,.2,1)",
            }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-xl font-extrabold text-slate-900 leading-none">
            {fmt(value)}
          </span>
          <span className="text-[10px] font-medium" style={{ color }}>
            {Math.round(pct * 100)}%
          </span>
        </div>
      </div>
      <span className="text-xs font-semibold text-slate-500">{label}</span>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════ PROGRESS ROW */
const PRow = ({ label, count, max, color, onClick }) => {
  const pct = max > 0 ? Math.min((count / max) * 100, 100) : 0;
  return (
    <div
      onClick={onClick}
      className={`mb-2.5 ${onClick ? "cursor-pointer group" : ""}`}
    >
      <div className="flex justify-between text-xs mb-1">
        <span
          className={`font-medium text-slate-600 truncate pr-2 max-w-[70%] ${onClick ? "group-hover:text-indigo-600 transition-colors" : ""}`}
        >
          {label}
        </span>
        <span className="font-bold text-slate-800 flex-shrink-0">
          {fmt(count)}
        </span>
      </div>
      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
        <motion.div
          className="h-full rounded-full"
          style={{ background: color }}
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        />
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════ KPI CARD */
const PALETTES = {
  indigo: {
    accent: "#4F46E5",
    light: "#EEF2FF",
    text: "#4F46E5",
    ring: "rgba(79,70,229,0.15)",
  },
  violet: {
    accent: "#7C3AED",
    light: "#F5F3FF",
    text: "#7C3AED",
    ring: "rgba(124,58,237,0.15)",
  },
  emerald: {
    accent: "#059669",
    light: "#ECFDF5",
    text: "#059669",
    ring: "rgba(5,150,105,0.15)",
  },
  rose: {
    accent: "#E11D48",
    light: "#FFF1F2",
    text: "#E11D48",
    ring: "rgba(225,29,72,0.15)",
  },
  amber: {
    accent: "#D97706",
    light: "#FFFBEB",
    text: "#D97706",
    ring: "rgba(217,119,6,0.15)",
  },
  sky: {
    accent: "#0284C7",
    light: "#F0F9FF",
    text: "#0284C7",
    ring: "rgba(2,132,199,0.15)",
  },
};

function KPICard({
  icon: Icon,
  label,
  value,
  sub,
  color = "indigo",
  onClick,
  loading,
  trend,
  trendPos,
  sparkData = [],
  sparkBasis = "",
}) {
  const p = PALETTES[color] || PALETTES.indigo;
  const chartData = sparkData.map((d, i) => {
    if (typeof d === "object" && d !== null) {
      return { i, label: d.label || `Point ${i + 1}`, v: d.v ?? 0 };
    }
    return { i, label: `Point ${i + 1}`, v: typeof d === "number" ? d : 0 };
  });

  return (
    <Card onClick={onClick} className="p-5 relative overflow-hidden group">
      {/* gradient accent top line */}
      <div
        className="absolute top-0 left-0 right-0 h-[3.5px] rounded-t-2xl"
        style={{
          background: `linear-gradient(90deg, ${p.accent}, ${p.accent}44)`,
        }}
      />
      <div className="flex items-start justify-between mb-3">
        <div className="p-2.5 rounded-xl shadow-2xs" style={{ background: p.light }}>
          <Icon className="text-lg" style={{ color: p.accent }} />
        </div>
        {trend !== undefined && !loading && (
          <span
            className="flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full border border-slate-100 dark:border-slate-700/50"
            style={{
              background: trendPos ? "#ECFDF5" : "#FFF1F2",
              color: trendPos ? "#059669" : "#E11D48",
            }}
          >
            {trendPos ? (
              <FaArrowUp className="text-[9px]" />
            ) : (
              <FaArrowDown className="text-[9px]" />
            )}
            {trend}
          </span>
        )}
      </div>

      {loading ? (
        <>
          <SK cls="h-9 w-20 mb-2" />
          <SK cls="h-3.5 w-32" />
        </>
      ) : (
        <>
          <div className="text-[28px] font-extrabold text-slate-900 dark:text-white tracking-tight leading-none mb-1">
            {value}
          </div>
          <div className="text-[13px] font-bold text-slate-700 dark:text-slate-300">
            {label}
          </div>
          {sub && (
            <div className="text-[11px] font-medium text-slate-500 dark:text-slate-400 mt-0.5 truncate">
              {sub}
            </div>
          )}
        </>
      )}

      {/* Mini sparkline with Data Basis text */}
      {!loading && chartData.length > 0 && (
        <div className="mt-3 pt-2.5 border-t border-slate-100 dark:border-slate-800 flex flex-col justify-end">
          {sparkBasis && (
            <div className="flex items-center justify-between text-[10px] font-semibold text-slate-500 dark:text-slate-400 mb-1">
              <span className="inline-flex items-center gap-1 bg-slate-50 dark:bg-slate-800/80 border border-slate-200/80 dark:border-slate-700/70 px-2 py-0.5 rounded-md text-[10px] text-slate-600 dark:text-slate-300 font-medium">
                <FaChartLine className="text-[9px]" style={{ color: p.accent }} />
                <span>{sparkBasis}</span>
              </span>
            </div>
          )}
          <ResponsiveContainer width="100%" height={42}>
            <AreaChart
              data={chartData}
              margin={{ top: 4, right: 2, left: 2, bottom: 2 }}
            >
              <defs>
                <linearGradient id={`kpi-${color}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={p.accent} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={p.accent} stopOpacity={0.0} />
                </linearGradient>
              </defs>
              <Tooltip
                content={({ active, payload }) => {
                  if (active && payload && payload.length) {
                    const item = payload[0].payload;
                    return (
                      <div className="bg-slate-900 text-white text-[11px] font-medium px-2.5 py-1 rounded-lg shadow-lg border border-slate-800">
                        <span className="text-slate-300">{item.label}: </span>
                        <span className="font-bold text-white">
                          {typeof item.v === "number" && item.v > 999 ? fmtC(item.v) : fmt(item.v)}
                        </span>
                      </div>
                    );
                  }
                  return null;
                }}
              />
              <Area
                type="monotone"
                dataKey="v"
                stroke={p.accent}
                strokeWidth={2}
                fill={`url(#kpi-${color})`}
                dot={false}
                activeDot={{ r: 4, stroke: "#fff", strokeWidth: 1.5, fill: p.accent }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {onClick && (
        <div className="absolute bottom-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
          <FaChevronRight className="text-slate-400 dark:text-slate-500 text-xs" />
        </div>
      )}
    </Card>
  );
}

/* ═══════════════════════════════════════════════════════════ CUSTOM TOOLTIP */
const ChartTip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-slate-900 text-white text-xs rounded-xl px-3 py-2 shadow-xl space-y-1">
      <p className="font-bold text-slate-300">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }}>
          {p.name}: {fmtC(p.value)}
        </p>
      ))}
    </div>
  );
};

const AttTip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-slate-900 text-white text-xs rounded-xl px-3 py-2 shadow-xl">
      <p className="font-bold text-slate-300">{label}</p>
      <p className="text-emerald-400">Present: {payload[0]?.value ?? 0}</p>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════ QUICK ACTION */
const QA = ({ icon: Icon, label, gradient, onClick }) => (
  <motion.button
    onClick={onClick}
    whileHover={{ y: -2, scale: 1.03 }}
    whileTap={{ scale: 0.97 }}
    className="flex flex-col items-center gap-2 p-3 rounded-xl border border-slate-100
      hover:border-transparent hover:shadow-md transition-all duration-150 bg-white text-center"
  >
    <div
      className={`w-10 h-10 rounded-xl flex items-center justify-center shadow-sm ${gradient}`}
    >
      <Icon className="text-white text-base" />
    </div>
    <span className="text-[11px] font-semibold text-slate-500 leading-tight">
      {label}
    </span>
  </motion.button>
);

/* ═══════════════════════════════════════════════════════════ ROW ITEM */
const RowItem = ({ left, right, onClick, children }) => (
  <motion.div
    onClick={onClick}
    whileHover={onClick ? { backgroundColor: "#F8FAFC" } : {}}
    className={`flex items-center gap-3 px-5 py-3 border-b border-slate-50 last:border-0 ${onClick ? "cursor-pointer group" : ""}`}
  >
    {left}
    <div className="flex-1 min-w-0">{children}</div>
    {right}
  </motion.div>
);

/* ═══════════════════════════════════════════════════════════ MAIN */
export default function Homepage() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshed, setRefreshed] = useState(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(`${server}/dashboard`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(r.data.data);
      setRefreshed(new Date());
    } catch {
      setError("Failed to load dashboard data.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  /* ── derived ── */
  const C = data?.counts || {};
  const Fi = data?.finance || {};
  const GB = data?.genderBreakdown || {};
  const TA = data?.todayAttendance || [];

  const present = TA.find((t) => t.status === "Present")?.count || 0;
  const absent = TA.find((t) => t.status === "Absent")?.count || 0;
  const late = TA.find((t) => t.status === "Late")?.count || 0;
  const empTotal = C.activeEmployees || 1;

  const wkData = (data?.weeklyAttendanceChart || []).map((d) => ({
    day: d.dayName,
    v: d.present || 0,
  }));

  const mnData = (data?.monthlyFinanceChart || []).map((d) => ({
    name: d.label?.split(" ")[0] || "",
    fee: d.feeCollection ?? 0,
    exp: d.expenses ?? 0,
  }));

  const feeSpark = (data?.monthlyFinanceChart || []).map(
    (d) => ({ label: d.label, v: d.feeCollection ?? 0 })
  );
  const expSpark = (data?.monthlyFinanceChart || []).map(
    (d) => ({ label: d.label, v: d.expenses ?? 0 })
  );
  const attSpark = (data?.weeklyAttendanceChart || []).map(
    (d) => ({ label: d.dayName, v: d.present || 0 })
  );

  const isMonthlyStu = (data?.monthlyStudentChart || []).some((d) => (d.count ?? 0) > 0);
  const stuSpark = isMonthlyStu
    ? (data?.monthlyStudentChart || []).map((d) => ({ label: d.label, v: d.count ?? 0 }))
    : (data?.studentsByClass || []).map((c) => ({ label: `Class ${c.class}`, v: c.count ?? 0 }));

  const stuSparkBasis = isMonthlyStu
    ? "Basis: 6-Month Admissions Trend"
    : "Basis: Class-wise Distribution";
  const attSparkBasis = "Basis: 7-Day Staff Attendance";
  const feeSparkBasis = "Basis: 6-Month Fee Collection";
  const expSparkBasis = "Basis: 6-Month Expense Trend";

  const byClass = [...(data?.studentsByClass || [])].sort((a, b) =>
    a.class.localeCompare(b.class, undefined, { numeric: true }),
  );
  const maxCls = Math.max(...byClass.map((c) => c.count), 1);

  const byDept = data?.employeesByDept || [];
  const maxDept = Math.max(...byDept.map((d) => d.count), 1);

  const feeStatuses = data?.feeStatus || [];
  const feeMap = Object.fromEntries(feeStatuses.map((f) => [f.status, f]));
  const totalAsgn = feeStatuses.reduce((s, f) => s + (f.total ?? 0), 0);
  const expCats = data?.expenseByCategory || [];

  /* gender donut data */
  const stuGender = [
    { name: "Male", value: GB.students?.male || 0, color: "#4F46E5" },
    { name: "Female", value: GB.students?.female || 0, color: "#E11D48" },
  ].filter((d) => d.value > 0);

  const feeDonut = [
    { name: "Paid", value: feeMap["Paid"]?.count || 0, color: "#059669" },
    { name: "Partial", value: feeMap["Partial"]?.count || 0, color: "#D97706" },
    { name: "Pending", value: feeMap["Pending"]?.count || 0, color: "#E11D48" },
  ].filter((d) => d.value > 0);

  const today = new Date().toLocaleDateString("en-IN", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  /* ── animation variants ── */
  const containerVariants = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.07 } },
  };
  const itemVariants = {
    hidden: { opacity: 0, y: 14 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4, ease: "easeOut" },
    },
  };

  return (
    <div
      className="min-h-screen p-4 max-w-[1400px] mx-auto space-y-4 font-sans text-gray-800 dark:text-slate-100 transition-colors duration-300"
      style={{ fontFamily: "'Inter',system-ui,sans-serif" }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
        @keyframes shimmer {
          0%   { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        ::-webkit-scrollbar { width: 4px; height: 4px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: #CBD5E1; border-radius: 4px; }
      `}</style>

      {/* ══════════════════════ HERO ══════════════════════ */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className="rounded-2xl p-6 relative overflow-hidden bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-800 shadow-sm dark:shadow-xl transition-all"
      >
        {/* top accent gradient bar */}
        <div
          className="absolute top-0 left-0 right-0 h-[4px] rounded-t-2xl"
          style={{
            background:
              "linear-gradient(90deg, #4F46E5 0%, #7C3AED 40%, #059669 100%)",
          }}
        />

        <div className="relative z-10 flex flex-col lg:flex-row lg:items-center justify-between gap-6">
          {/* greeting & header */}
          <div>
            <div className="flex items-center gap-2 mb-1.5">
              <span className="inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-0.5 rounded-full bg-indigo-50 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-400 border border-indigo-200/60 dark:border-indigo-500/30">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 dark:bg-emerald-400 animate-pulse" />
                Live Academic Portal
              </span>
              <span className="text-slate-500 dark:text-slate-400 text-xs font-medium">
                • {greetMsg()},{" "}
                <strong className="text-slate-800 dark:text-slate-100">
                  {user?.username || "Admin"}
                </strong>{" "}
                👋
              </span>
            </div>
            <h1 className="text-2xl lg:text-3xl font-black text-slate-900 dark:text-white tracking-tight">
              School Dashboard
            </h1>
            <p className="text-slate-500 dark:text-slate-400 text-xs mt-1 font-medium flex items-center gap-1.5">
              <span>📅 {today}</span>
            </p>
          </div>

          {/* stat pills */}
          <div className="flex flex-wrap items-center gap-3">
            {[
              {
                label: "Students",
                val: loading ? "…" : fmt(C.totalStudents),
                icon: FaUserGraduate,
                lightBg: "bg-indigo-50/80 border-indigo-100",
                darkBg: "dark:bg-slate-800/80 dark:border-indigo-500/30",
                color: "#4F46E5",
              },
              {
                label: "Employees",
                val: loading ? "…" : fmt(C.totalEmployees),
                icon: FaChalkboardTeacher,
                lightBg: "bg-purple-50/80 border-purple-100",
                darkBg: "dark:bg-slate-800/80 dark:border-purple-500/30",
                color: "#7C3AED",
              },
              {
                label: "Present",
                val: loading ? "…" : fmt(present),
                icon: FaCheckCircle,
                lightBg: "bg-emerald-50/80 border-emerald-100",
                darkBg: "dark:bg-slate-800/80 dark:border-emerald-500/30",
                color: "#059669",
              },
              {
                label: "Notices",
                val: loading ? "…" : fmt(C.activeNotices),
                icon: FaBullhorn,
                lightBg: "bg-rose-50/80 border-rose-100",
                darkBg: "dark:bg-slate-800/80 dark:border-rose-500/30",
                color: "#E11D48",
              },
            ].map((s, i) => (
              <div
                key={i}
                className={`flex items-center gap-3 rounded-xl px-4 py-2.5 border ${s.lightBg} ${s.darkBg} shadow-2xs transition-transform hover:scale-105`}
              >
                <div
                  className="p-1.5 rounded-lg bg-white dark:bg-slate-900 shadow-2xs flex items-center justify-center"
                  style={{ color: s.color }}
                >
                  <s.icon className="text-sm" />
                </div>
                <div>
                  <div className="text-slate-900 dark:text-white text-base font-black leading-none">
                    {s.val}
                  </div>
                  <div className="text-slate-600 dark:text-slate-400 text-[10px] font-semibold mt-0.5">
                    {s.label}
                  </div>
                </div>
              </div>
            ))}

            <motion.button
              onClick={load}
              disabled={loading}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="flex items-center gap-2 border border-slate-200 dark:border-slate-700 px-4 py-2.5 rounded-xl
                text-xs font-bold text-slate-700 dark:text-slate-200 transition-all disabled:opacity-60 bg-slate-50 hover:bg-slate-100 dark:bg-slate-800/90 dark:hover:bg-slate-700
                shadow-2xs"
            >
              <FaSyncAlt
                className={`text-xs text-indigo-600 dark:text-indigo-400 ${loading ? "animate-spin" : ""}`}
              />
              {loading ? "Refreshing…" : "Refresh"}
            </motion.button>
          </div>
        </div>

        {refreshed && (
          <p className="relative z-10 text-slate-400 dark:text-slate-400 text-[10px] font-medium mt-3 text-right">
            Last updated {ago(refreshed)}
          </p>
        )}
      </motion.div>

      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="bg-rose-50 border border-rose-200 text-rose-700 text-sm rounded-xl px-4 py-3 flex items-center gap-2"
          >
            <FaExclamationTriangle className="flex-shrink-0" />
            {error}
            <button
              onClick={load}
              className="ml-auto font-bold underline text-xs"
            >
              Retry
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ══════════════════════ KPI CARDS ══════════════════════ */}
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="grid grid-cols-2 xl:grid-cols-4 gap-4"
      >
        {[
          {
            icon: FaUserGraduate,
            color: "indigo",
            label: "Total Students",
            value: fmt(C.totalStudents),
            sub: `${fmt(C.activeStudents)} active`,
            spark: stuSpark,
            sparkBasis: stuSparkBasis,
            trend: `${fmt(C.activeStudents)} active`,
            tPos: true,
            path: "students",
          },
          {
            icon: FaChalkboardTeacher,
            color: "violet",
            label: "Teaching Staff",
            value: fmt(C.totalEmployees),
            sub: `${fmt(C.totalDepartments)} departments`,
            spark: attSpark,
            sparkBasis: attSparkBasis,
            trend: `${fmt(C.activeEmployees)} active`,
            tPos: true,
            path: "employees",
          },
          {
            icon: FaMoneyBillWave,
            color: "emerald",
            label: "Fee Collected",
            value: fmtC(Fi.monthFeeCollected),
            sub: `Pending ${fmtC(Fi.pendingFees)}`,
            spark: feeSpark,
            sparkBasis: feeSparkBasis,
            trend: fmtC(Fi.monthNetBalance),
            tPos: (Fi.monthNetBalance ?? 0) >= 0,
            path: "student-fees",
          },
          {
            icon: FaReceipt,
            color: "rose",
            label: "Monthly Expense",
            value: fmtC(Fi.monthExpenses),
            sub: `All‑time ${fmtC(Fi.totalExpenses)}`,
            spark: expSpark,
            sparkBasis: expSparkBasis,
            trend: "This month",
            tPos: false,
            path: "expenses",
          },
        ].map((k, i) => (
          <motion.div key={i} variants={itemVariants}>
            <KPICard
              loading={loading}
              icon={k.icon}
              color={k.color}
              label={k.label}
              value={loading ? "—" : k.value}
              sub={k.sub}
              sparkData={k.spark}
              sparkBasis={k.sparkBasis}
              trend={k.trend}
              trendPos={k.tPos}
              onClick={() => navigate(k.path)}
            />
          </motion.div>
        ))}
      </motion.div>

      {/* ══════════════════════ FINANCE STRIP ══════════════════════ */}
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="grid grid-cols-1 sm:grid-cols-3 gap-4"
      >
        {[
          {
            label: "Fee Collected (Month)",
            val: Fi.monthFeeCollected,
            sub: `Total ${fmtC(Fi.totalFeeCollected)}`,
            icon: FaMoneyBillWave,
            accentColor: "#059669",
            lightBg: "linear-gradient(135deg, #ECFDF5 0%, #FFFFFF 100%)",
            lightBorder: "#A7F3D0",
            path: "student-fees",
          },
          {
            label: "Expenses (Month)",
            val: Fi.monthExpenses,
            sub: `Total ${fmtC(Fi.totalExpenses)}`,
            icon: FaReceipt,
            accentColor: "#E11D48",
            lightBg: "linear-gradient(135deg, #FFF1F2 0%, #FFFFFF 100%)",
            lightBorder: "#FECDD3",
            path: "expenses",
          },
          {
            label: "Net Balance (Month)",
            val: Fi.monthNetBalance,
            sub: (Fi.monthNetBalance ?? 0) >= 0 ? "Surplus ✓" : "Deficit ⚠",
            icon: FaWallet,
            accentColor: (Fi.monthNetBalance ?? 0) >= 0 ? "#4F46E5" : "#D97706",
            lightBg: (Fi.monthNetBalance ?? 0) >= 0
              ? "linear-gradient(135deg, #EEF2FF 0%, #FFFFFF 100%)"
              : "linear-gradient(135deg, #FFFBEB 0%, #FFFFFF 100%)",
            lightBorder: (Fi.monthNetBalance ?? 0) >= 0 ? "#C7D2FE" : "#FDE68A",
            path: "student-fees",
          },
        ].map((f, i) => (
          <motion.div key={i} variants={itemVariants}>
            <motion.div
              onClick={() => navigate(f.path)}
              whileHover={{
                y: -3,
                boxShadow: `0 12px 28px -6px ${f.accentColor}33`,
              }}
              transition={{ duration: 0.18 }}
              className="rounded-2xl px-5 py-4 cursor-pointer overflow-hidden relative border transition-all duration-200 bg-white dark:bg-slate-900 border-slate-200/80 dark:border-slate-800"
              style={{
                boxShadow: "0 4px 16px -4px rgba(0,0,0,0.06)",
              }}
            >
              {/* top accent gradient bar */}
              <div
                className="absolute top-0 left-0 right-0 h-[3.5px] rounded-t-2xl"
                style={{
                  background: f.accentColor,
                }}
              />
              <div className="flex items-center justify-between mb-2">
                <p className="text-slate-600 dark:text-slate-300 text-xs font-semibold">
                  {f.label}
                </p>
                <div
                  className="w-8 h-8 rounded-xl flex items-center justify-center shadow-2xs"
                  style={{ background: `${f.accentColor}18`, color: f.accentColor }}
                >
                  <f.icon className="text-sm" />
                </div>
              </div>
              {loading ? (
                <div className="h-7 w-28 rounded-lg bg-slate-100 dark:bg-slate-800 animate-pulse mb-1" />
              ) : (
                <p className="text-2xl font-black tracking-tight text-slate-900 dark:text-white">
                  {fmtC(f.val)}
                </p>
              )}
              {!loading && (
                <p className="text-[11px] font-bold mt-1 flex items-center gap-1" style={{ color: f.accentColor }}>
                  {f.sub}
                </p>
              )}
            </motion.div>
          </motion.div>
        ))}
      </motion.div>

      {/* ══════════════════════ MAIN GRID ══════════════════════ */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
        {/* ── LEFT COLUMN (2/3) ── */}
        <div className="xl:col-span-2 space-y-5">
          {/* REVENUE vs EXPENSE AREA CHART */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Card>
              <CardHeader
                title="Revenue vs Expense Trend"
                sub="Fee collection compared to expenses — last 6 months"
                action={() => navigate("expenses/dashboard")}
              />
              <div className="p-5">
                <div className="flex items-center gap-6 mb-4">
                  <span className="flex items-center gap-2 text-xs font-semibold text-slate-500">
                    <span className="w-5 h-0.5 rounded-full bg-indigo-500 inline-block" />
                    Fee Collection
                  </span>
                  <span className="flex items-center gap-2 text-xs font-semibold text-slate-500">
                    <span
                      className="w-5 h-0.5 rounded-full bg-rose-400 inline-block"
                      style={{ borderTop: "2px dashed #F43F5E" }}
                    />
                    Expenses
                  </span>
                  {!loading && (
                    <span className="ml-auto text-xs font-bold text-emerald-600">
                      Net: {fmtC(Fi.netBalance)}
                    </span>
                  )}
                </div>
                {loading ? (
                  <SK cls="h-48 w-full" />
                ) : (
                  <ResponsiveContainer width="100%" height={200}>
                    <AreaChart
                      data={mnData}
                      margin={{ top: 4, right: 4, left: 0, bottom: 0 }}
                    >
                      <defs>
                        <linearGradient id="gFee" x1="0" y1="0" x2="0" y2="1">
                          <stop
                            offset="0%"
                            stopColor="#4F46E5"
                            stopOpacity={0.2}
                          />
                          <stop
                            offset="100%"
                            stopColor="#4F46E5"
                            stopOpacity={0}
                          />
                        </linearGradient>
                        <linearGradient id="gExp" x1="0" y1="0" x2="0" y2="1">
                          <stop
                            offset="0%"
                            stopColor="#E11D48"
                            stopOpacity={0.15}
                          />
                          <stop
                            offset="100%"
                            stopColor="#E11D48"
                            stopOpacity={0}
                          />
                        </linearGradient>
                      </defs>
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="#F1F5F9"
                        vertical={false}
                      />
                      <XAxis
                        dataKey="name"
                        tick={{
                          fontSize: 11,
                          fill: "#94A3B8",
                          fontWeight: 500,
                        }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <YAxis
                        tick={{ fontSize: 10, fill: "#94A3B8" }}
                        axisLine={false}
                        tickLine={false}
                        tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`}
                        width={45}
                      />
                      <Tooltip content={<ChartTip />} />
                      <Area
                        type="monotone"
                        dataKey="exp"
                        name="Expenses"
                        stroke="#F43F5E"
                        strokeWidth={1.5}
                        strokeDasharray="4 3"
                        fill="url(#gExp)"
                        dot={false}
                      />
                      <Area
                        type="monotone"
                        dataKey="fee"
                        name="Fee"
                        stroke="#4F46E5"
                        strokeWidth={2}
                        fill="url(#gFee)"
                        dot={{ r: 3.5, fill: "#4F46E5", strokeWidth: 0 }}
                        activeDot={{ r: 5, fill: "#4F46E5" }}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                )}
              </div>
            </Card>
          </motion.div>

          {/* ATTENDANCE + FEE STATUS */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* Attendance */}
            <Card>
              <CardHeader
                title="Today's Attendance"
                sub="Employee attendance breakdown"
                action={() => navigate("employees/attendance")}
                actionLabel="Mark"
              />
              <div className="p-5">
                {loading ? (
                  <SK cls="h-36" />
                ) : (
                  <>
                    <div className="flex items-center justify-around mb-5">
                      <Ring
                        value={present}
                        max={empTotal}
                        color="#059669"
                        label="Present"
                        size={78}
                        stroke={8}
                      />
                      <Ring
                        value={absent}
                        max={empTotal}
                        color="#E11D48"
                        label="Absent"
                        size={78}
                        stroke={8}
                      />
                      <Ring
                        value={late}
                        max={empTotal}
                        color="#D97706"
                        label="Late"
                        size={78}
                        stroke={8}
                      />
                    </div>
                    <div className="border-t border-slate-50 pt-3">
                      <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-2">
                        Weekly Trend
                      </p>
                      {wkData.length > 0 ? (
                        <ResponsiveContainer width="100%" height={44}>
                          <BarChart
                            data={wkData}
                            margin={{ top: 0, right: 0, left: 0, bottom: 0 }}
                          >
                            <Bar
                              dataKey="v"
                              fill="#818CF8"
                              radius={[3, 3, 0, 0]}
                              maxBarSize={24}
                            />
                            <XAxis
                              dataKey="day"
                              tick={{ fontSize: 9, fill: "#94A3B8" }}
                              axisLine={false}
                              tickLine={false}
                            />
                            <Tooltip
                              content={<AttTip />}
                              cursor={{ fill: "#F8FAFC" }}
                            />
                          </BarChart>
                        </ResponsiveContainer>
                      ) : (
                        <p className="text-xs text-slate-300 text-center py-2">
                          No data this week
                        </p>
                      )}
                    </div>
                  </>
                )}
              </div>
            </Card>

            {/* Fee Status */}
            <Card>
              <CardHeader
                title="Fee Status"
                sub="Paid / Partial / Pending"
                action={() => navigate("student-fees")}
              />
              <div className="p-5">
                {loading ? (
                  <SK cls="h-36" />
                ) : (
                  <div className="flex gap-5 items-center">
                    {/* Donut */}
                    <div className="flex-shrink-0">
                      <PieChart width={100} height={100}>
                        <Pie
                          data={feeDonut}
                          dataKey="value"
                          innerRadius={30}
                          outerRadius={48}
                          paddingAngle={2}
                          strokeWidth={0}
                        >
                          {feeDonut.map((e, i) => (
                            <Cell key={i} fill={e.color} />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(v) => fmt(v)}
                          contentStyle={{
                            borderRadius: 12,
                            border: "none",
                            boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
                            fontSize: 11,
                          }}
                        />
                      </PieChart>
                    </div>

                    {/* Legend & bars */}
                    <div className="flex-1 space-y-2.5">
                      {[
                        { k: "Paid", l: "Paid", c: "#059669" },
                        { k: "Partial", l: "Partial", c: "#D97706" },
                        { k: "Pending", l: "Pending", c: "#E11D48" },
                      ].map((s) => {
                        const rec = feeMap[s.k];
                        return (
                          <div
                            key={s.k}
                            onClick={() => navigate("student-fees")}
                            className="cursor-pointer group"
                          >
                            <div className="flex justify-between text-xs mb-0.5">
                              <span className="font-medium text-slate-500 flex items-center gap-1.5 group-hover:text-indigo-600 transition-colors">
                                <span
                                  className="w-2 h-2 rounded-full"
                                  style={{ background: s.c }}
                                />
                                {s.l}
                              </span>
                              <span className="font-bold text-slate-800">
                                {fmt(rec?.count ?? 0)}
                              </span>
                            </div>
                            <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                              <motion.div
                                className="h-full rounded-full"
                                style={{ background: s.c }}
                                initial={{ width: 0 }}
                                animate={{
                                  width:
                                    totalAsgn > 0
                                      ? `${((rec?.total ?? 0) / totalAsgn) * 100}%`
                                      : "0%",
                                }}
                                transition={{ duration: 0.9, ease: "easeOut" }}
                              />
                            </div>
                          </div>
                        );
                      })}
                      <p className="text-[10px] text-slate-400 flex justify-between pt-1.5 border-t border-slate-50">
                        <span>Total Assigned</span>
                        <span className="font-bold text-slate-700">
                          {fmtC(totalAsgn)}
                        </span>
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </Card>
          </div>

          {/* STUDENTS + EMPLOYEES BARS */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Card>
              <CardHeader
                title="Students by Class"
                sub="Enrollment trend & distribution"
                action={() => navigate("students")}
              />
              <div className="p-5">
                {loading ? (
                  <div className="space-y-2">
                    {[...Array(5)].map((_, i) => (
                      <SK key={i} cls="h-5" />
                    ))}
                  </div>
                ) : byClass.length === 0 ? (
                  <p className="text-xs text-slate-300 text-center py-6">
                    No student records
                  </p>
                ) : (
                  <>
                    <div className="mb-3">
                      <ResponsiveContainer width="100%" height={90}>
                        <LineChart
                          data={byClass.map((c) => ({
                            class: `Class ${c.class}`,
                            count: c.count,
                          }))}
                          margin={{ top: 5, right: 10, left: -20, bottom: 0 }}
                        >
                          <CartesianGrid
                            strokeDasharray="3 3"
                            stroke="#F1F5F9"
                            vertical={false}
                          />
                          <XAxis
                            dataKey="class"
                            tick={{ fontSize: 9, fill: "#94A3B8" }}
                            axisLine={false}
                            tickLine={false}
                          />
                          <YAxis
                            tick={{ fontSize: 9, fill: "#94A3B8" }}
                            axisLine={false}
                            tickLine={false}
                          />
                          <Tooltip
                            contentStyle={{
                              borderRadius: 10,
                              border: "none",
                              boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
                              fontSize: 11,
                            }}
                            formatter={(val) => [`${val} Students`, "Count"]}
                          />
                          <Line
                            type="monotone"
                            dataKey="count"
                            stroke="#4F46E5"
                            strokeWidth={2.5}
                            dot={{ r: 3, fill: "#4F46E5" }}
                            activeDot={{ r: 5 }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="max-h-36 overflow-y-auto space-y-0.5 pr-1 border-t border-slate-50 pt-2">
                      {byClass.map((cl, i) => (
                        <PRow
                          key={i}
                          label={`Class ${cl.class}`}
                          count={cl.count}
                          max={maxCls}
                          color="#4F46E5"
                          onClick={() => navigate("students")}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>
            </Card>

            <Card>
              <CardHeader
                title="Staff by Department"
                sub="Active employees"
                action={() => navigate("departments")}
              />
              <div className="p-5">
                {loading ? (
                  <div className="space-y-2">
                    {[...Array(5)].map((_, i) => (
                      <SK key={i} cls="h-5" />
                    ))}
                  </div>
                ) : byDept.length === 0 ? (
                  <p className="text-xs text-slate-300 text-center py-6">
                    No department data
                  </p>
                ) : (
                  <div className="max-h-48 overflow-y-auto space-y-0.5 pr-1">
                    {byDept.map((d, i) => (
                      <PRow
                        key={i}
                        label={
                          typeof d.department === "object"
                            ? d.department?.name || "Unassigned"
                            : d.department || "Unassigned"
                        }
                        count={d.count}
                        max={maxDept}
                        color="#7C3AED"
                        onClick={() => navigate("employees")}
                      />
                    ))}
                  </div>
                )}
              </div>
            </Card>
          </div>

          {/* EXPENSE + GENDER */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* Expense by category */}
            <Card>
              <CardHeader
                title="Expense Breakdown"
                sub="This month by category"
                action={() => navigate("expenses/dashboard")}
              />
              <div className="p-5">
                {loading ? (
                  <SK cls="h-36" />
                ) : expCats.length === 0 ? (
                  <p className="text-xs text-slate-300 text-center py-6">
                    No expenses this month
                  </p>
                ) : (
                  <div className="flex items-center gap-4">
                    <PieChart width={88} height={88}>
                      <Pie
                        data={expCats}
                        dataKey="total"
                        innerRadius={26}
                        outerRadius={42}
                        paddingAngle={2}
                        strokeWidth={0}
                      >
                        {expCats.map((_, i) => (
                          <Cell
                            key={i}
                            fill={
                              [
                                "#4F46E5",
                                "#7C3AED",
                                "#D97706",
                                "#059669",
                                "#E11D48",
                                "#0284C7",
                              ][i % 6]
                            }
                          />
                        ))}
                      </Pie>
                    </PieChart>
                    <div className="flex-1 space-y-1.5">
                      {expCats.slice(0, 5).map((cat, i) => {
                        const clr = [
                          "#4F46E5",
                          "#7C3AED",
                          "#D97706",
                          "#059669",
                          "#E11D48",
                          "#0284C7",
                        ][i % 6];
                        return (
                          <div
                            key={i}
                            onClick={() => navigate("expenses")}
                            className="flex items-center justify-between cursor-pointer hover:bg-slate-50 rounded-lg px-1.5 py-0.5 -mx-1.5 transition-colors"
                          >
                            <span className="flex items-center gap-1.5 min-w-0">
                              <span
                                className="w-2 h-2 rounded-full flex-shrink-0"
                                style={{ background: clr }}
                              />
                              <span className="text-xs text-slate-600 truncate">
                                {typeof cat.category === "object" ? (cat.category?.name || "Other") : (cat.category || "Other")}
                              </span>
                            </span>
                            <span className="text-xs font-bold text-slate-900 ml-2">
                              {fmtC(cat.total)}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </Card>

            {/* Gender overview */}
            <Card>
              <CardHeader
                title="Gender Overview"
                sub="Students and employees"
              />
              <div className="p-5">
                {loading ? (
                  <SK cls="h-36" />
                ) : (
                  <div className="space-y-4">
                    {[
                      {
                        label: "Students",
                        d: GB.students || {},
                        total: C.totalStudents,
                        path: "students",
                      },
                      {
                        label: "Employees",
                        d: GB.employees || {},
                        total: C.totalEmployees,
                        path: "employees",
                      },
                    ].map((g) => (
                      <div
                        key={g.label}
                        onClick={() => navigate(g.path)}
                        className="cursor-pointer hover:bg-slate-50 rounded-xl p-2 -mx-2 transition-colors"
                      >
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">
                          {g.label}
                        </p>
                        <div className="grid grid-cols-3 gap-2 text-center">
                          {[
                            {
                              ic: FaMale,
                              lb: "Male",
                              v: g.d.male,
                              bg: "#EEF2FF",
                              cl: "#4F46E5",
                            },
                            {
                              ic: FaFemale,
                              lb: "Female",
                              v: g.d.female,
                              bg: "#FFF1F2",
                              cl: "#E11D48",
                            },
                            {
                              ic: FaUsers,
                              lb: "Total",
                              v: g.total,
                              bg: "#F8FAFC",
                              cl: "#64748B",
                            },
                          ].map((s, i) => (
                            <div
                              key={i}
                              className="rounded-xl py-2.5"
                              style={{ background: s.bg }}
                            >
                              <s.ic
                                style={{ color: s.cl }}
                                className="mx-auto mb-0.5 text-sm"
                              />
                              <div className="text-sm font-extrabold text-slate-900 leading-none">
                                {fmt(s.v)}
                              </div>
                              <div className="text-[10px] text-slate-400 font-medium">
                                {s.lb}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Card>
          </div>

          {/* RECENT FEE PAYMENTS */}
          <Card>
            <CardHeader
              title="Recent Fee Payments"
              sub="Latest fee transactions"
              action={() => navigate("student-fees")}
            />
            {loading ? (
              <div className="p-5 space-y-3">
                {[...Array(4)].map((_, i) => (
                  <SK key={i} cls="h-10" />
                ))}
              </div>
            ) : (data?.recentFeePayments || []).length === 0 ? (
              <div className="p-5 text-center text-slate-300 text-sm py-10">
                No payments recorded
              </div>
            ) : (
              <div>
                {(data?.recentFeePayments || []).map((p, i) => (
                  <RowItem
                    key={i}
                    onClick={() => navigate("student-fees")}
                    left={
                      <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
                        <FaMoneyBillWave className="text-emerald-500 text-xs" />
                      </div>
                    }
                    right={
                      <span className="text-sm font-extrabold text-emerald-600">
                        {fmtC(p.amountPaid)}
                      </span>
                    }
                  >
                    <p className="text-[13px] font-semibold text-slate-800 truncate group-hover:text-indigo-600 transition-colors">
                      {p.studentName}
                    </p>
                    <p className="text-[11px] text-slate-400">
                      {p.studentClass} · {p.paymentMode} ·{" "}
                      {fmtDate(p.paymentDate)}
                    </p>
                  </RowItem>
                ))}
              </div>
            )}
          </Card>
        </div>

        {/* ── RIGHT COLUMN (1/3) ── */}
        <div className="space-y-5">
          {/* QUICK ACTIONS */}
          <Card>
            <CardHeader title="Quick Actions" sub="Common tasks" />
            <div className="p-4 grid grid-cols-3 gap-2">
              {[
                {
                  icon: FaUserGraduate,
                  label: "Add Student",
                  gradient: "bg-gradient-to-br from-indigo-500 to-blue-600",
                  path: "students/register",
                },
                {
                  icon: FaChalkboardTeacher,
                  label: "Add Employee",
                  gradient: "bg-gradient-to-br from-violet-500 to-purple-600",
                  path: "employees/register",
                },
                {
                  icon: FaMoneyBillWave,
                  label: "Collect Fee",
                  gradient: "bg-gradient-to-br from-emerald-500 to-green-600",
                  path: "student-fees",
                },
                {
                  icon: FaBullhorn,
                  label: "Notice",
                  gradient: "bg-gradient-to-br from-rose-500 to-red-600",
                  path: "notices",
                },
                {
                  icon: FaCalendarAlt,
                  label: "Add Event",
                  gradient: "bg-gradient-to-br from-amber-500 to-orange-500",
                  path: "events",
                },
                {
                  icon: FaClipboardList,
                  label: "Attendance",
                  gradient: "bg-gradient-to-br from-teal-500 to-cyan-500",
                  path: "employees/attendance",
                },
                {
                  icon: FaImages,
                  label: "Gallery",
                  gradient: "bg-gradient-to-br from-pink-500 to-rose-500",
                  path: "gallery",
                },
                {
                  icon: FaBuilding,
                  label: "Departments",
                  gradient: "bg-gradient-to-br from-sky-500 to-blue-500",
                  path: "departments",
                },
                {
                  icon: FaUsers,
                  label: "Users",
                  gradient: "bg-gradient-to-br from-slate-500 to-slate-600",
                  path: "users",
                },
              ].map((q) => (
                <QA
                  key={q.path}
                  icon={q.icon}
                  label={q.label}
                  gradient={q.gradient}
                  onClick={() => navigate(q.path)}
                />
              ))}
            </div>
          </Card>

          {/* UPCOMING EVENTS */}
          <Card>
            <CardHeader
              title="Upcoming Events"
              action={() => navigate("events")}
            />
            {loading ? (
              <div className="p-4 space-y-3">
                {[...Array(3)].map((_, i) => (
                  <SK key={i} cls="h-14" />
                ))}
              </div>
            ) : (data?.upcomingEventsList || []).length === 0 ? (
              <div className="py-10 text-center text-slate-300 text-xs">
                No upcoming events
              </div>
            ) : (
              (data?.upcomingEventsList || []).map((ev, i) => {
                const d = new Date(ev.eventDate);
                return (
                  <RowItem
                    key={i}
                    onClick={() => navigate("events")}
                    left={
                      <div className="bg-indigo-50 rounded-xl p-2 text-center min-w-[2.5rem] border border-indigo-100 flex-shrink-0">
                        <div className="text-[9px] font-bold text-indigo-500 uppercase leading-none">
                          {d.toLocaleDateString("en-IN", { month: "short" })}
                        </div>
                        <div className="text-xl font-extrabold text-slate-900 leading-none">
                          {d.getDate()}
                        </div>
                      </div>
                    }
                  >
                    <p className="text-[13px] font-bold text-slate-800 truncate group-hover:text-indigo-600 transition-colors">
                      {ev.title}
                    </p>
                    {ev.location && (
                      <p className="text-[11px] text-slate-400 truncate">
                        📍 {ev.location}
                      </p>
                    )}
                    <div className="mt-1">
                      <Badge text={ev.targetAudience} variant="blue" />
                    </div>
                  </RowItem>
                );
              })
            )}
          </Card>

          {/* RECENT NOTICES */}
          <Card>
            <CardHeader
              title="Recent Notices"
              action={() => navigate("notices")}
            />
            {loading ? (
              <div className="p-4 space-y-3">
                {[...Array(4)].map((_, i) => (
                  <SK key={i} cls="h-12" />
                ))}
              </div>
            ) : (data?.recentNotices || []).length === 0 ? (
              <div className="py-10 text-center text-slate-300 text-xs">
                No notices
              </div>
            ) : (
              (data?.recentNotices || []).map((n, i) => (
                <RowItem
                  key={i}
                  onClick={() => navigate("notices")}
                  left={
                    <div
                      className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 ${n.isImportant ? "bg-rose-50" : "bg-indigo-50"}`}
                    >
                      {n.isImportant ? (
                        <FaExclamationTriangle className="text-rose-500 text-[11px]" />
                      ) : (
                        <FaBullhorn className="text-indigo-400 text-[11px]" />
                      )}
                    </div>
                  }
                >
                  <p className="text-[13px] font-semibold text-slate-800 truncate group-hover:text-indigo-600 transition-colors">
                    {n.title}
                  </p>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <Badge
                      text={n.targetAudience}
                      variant={n.isImportant ? "red" : "blue"}
                    />
                    <span className="text-[10px] text-slate-300">
                      {ago(n.createdAt)}
                    </span>
                  </div>
                </RowItem>
              ))
            )}
          </Card>

          {/* RECENTLY ADMITTED */}
          <Card>
            <CardHeader
              title="Recently Admitted"
              sub="New student enrollments"
              action={() => navigate("students")}
            />
            {loading ? (
              <div className="p-4 space-y-3">
                {[...Array(4)].map((_, i) => (
                  <SK key={i} cls="h-10" />
                ))}
              </div>
            ) : (data?.recentStudents || []).length === 0 ? (
              <div className="py-10 text-center text-slate-300 text-xs">
                No recent admissions
              </div>
            ) : (
              (data?.recentStudents || []).map((s, i) => (
                <RowItem
                  key={i}
                  onClick={() => navigate("students")}
                  left={<Avatar name={s.studentName} gender={s.gender} />}
                  right={
                    <span className="text-[10px] text-slate-300 flex-shrink-0">
                      {ago(s.createdAt)}
                    </span>
                  }
                >
                  <p className="text-[13px] font-semibold text-slate-800 truncate group-hover:text-indigo-600 transition-colors">
                    {s.studentName}
                  </p>
                  <p className="text-[11px] text-slate-400">
                    Class {s.class}
                    {s.section ? `-${s.section}` : ""} · {s.admissionNumber}
                  </p>
                </RowItem>
              ))
            )}
          </Card>

          {/* RECENT EXPENSES */}
          <Card>
            <CardHeader
              title="Recent Expenses"
              action={() => navigate("expenses")}
            />
            {loading ? (
              <div className="p-4 space-y-3">
                {[...Array(4)].map((_, i) => (
                  <SK key={i} cls="h-10" />
                ))}
              </div>
            ) : (data?.recentExpenses || []).length === 0 ? (
              <div className="py-10 text-center text-slate-300 text-xs">
                No recent expenses
              </div>
            ) : (
              (data?.recentExpenses || []).map((e, i) => (
                <RowItem
                  key={i}
                  onClick={() => navigate("expenses")}
                  left={
                    <div className="w-8 h-8 rounded-xl bg-rose-50 flex items-center justify-center flex-shrink-0">
                      <FaReceipt className="text-rose-400 text-[11px]" />
                    </div>
                  }
                  right={
                    <span className="text-[13px] font-extrabold text-rose-500">
                      {fmtC(e.amount)}
                    </span>
                  }
                >
                  <p className="text-[13px] font-semibold text-slate-800 truncate group-hover:text-indigo-600 transition-colors">
                    {e.title}
                  </p>
                  <p className="text-[11px] text-slate-400">
                    {typeof e.category === "object" ? (e.category?.name || "General") : (e.category || "General")} · {fmtDate(e.date)}
                  </p>
                </RowItem>
              ))
            )}
          </Card>
        </div>
      </div>

      {/* compact footer */}
      <div className="border-t border-slate-100 pt-4 flex items-center justify-between text-[11px] text-slate-300">
        <span>© {new Date().getFullYear()} Dee Novo School · Admin Panel</span>
        <span>
          v2.0 · {refreshed ? `Updated ${ago(refreshed)}` : "Loading…"}
        </span>
      </div>
    </div>
  );
}
